<template>
  <div class="hvh-card--rounded v-sheet theme--light">
    <div class="pl-4 mb-3" style="display: flex; justify-content: space-between" v-if="!showSearch">
      <h2 class="pt-2">
        <template v-if="text">{{ text + menuOptionText }}</template>
      </h2>
      <div>
        <v-menu v-model="menu" bottom left origin="top right" style="z-index: 1001">
          <template v-slot:activator="{ on, attrs }">
            <v-btn class="my-1 mx-2" flat icon v-bind="attrs" v-on="on" @click.stop.prevent>
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <div @click.stop @mousedown.stop @mouseup.stop>
            <v-select :value="menuOptions.prop" :items="allChartTypes" label="Chart type" placeholder="Select an event..." @input="(skipNextFilter = true), $emit('top-event-changed', $event)" outline hide-details />
          </div>
          <v-card class="py-1">
            <v-list class="pb-0">
              <v-list-tile @click="(showSearch = true), (menu = false)">
                <v-flex style="min-width: 40px">
                  <v-btn flat icon style="margin-left: -6px">
                    <v-icon medium>mdi-magnify</v-icon>
                  </v-btn>
                </v-flex>
                <v-list-tile-title>Search</v-list-tile-title>
              </v-list-tile>
            </v-list>

            <v-divider></v-divider>
            <v-list class="pb-0">
              <v-list-tile>
                <v-list-tile-sub-title>Sort Order</v-list-tile-sub-title>
              </v-list-tile>
            </v-list>

            <v-list class="py-0">
              <v-list-tile @click="setUsePatientCount(false)">
                <v-flex style="min-width: 40px">
                  <v-icon v-if="!menuOptions.usePatientCount">mdi-check</v-icon>
                </v-flex>
                <v-list-tile-title>HCPs</v-list-tile-title>
              </v-list-tile>
            </v-list>

            <v-list class="py-0">
              <v-list-tile @click="setUsePatientCount(true)">
                <v-flex style="min-width: 40px">
                  <v-icon v-if="menuOptions.usePatientCount">mdi-check</v-icon>
                </v-flex>
                <v-list-tile-title>Patients</v-list-tile-title>
              </v-list-tile>
            </v-list>

            <template v-if="(menuOptions.levels || []).length">
              <v-divider></v-divider>
              <v-list class="pb-0">
                <v-list-tile>
                  <v-list-tile-sub-title>Select level of detail for chart</v-list-tile-sub-title>
                </v-list-tile>
              </v-list>

              <v-list class="py-0">
                <v-list-tile v-for="(option, ind) in menuOptions.levels" @[clickOption(option)]="selectOption('levels', option)" :key="ind + option.text">
                  <v-flex style="min-width: 40px">
                    <v-icon v-if="option.selected">mdi-check</v-icon>
                  </v-flex>
                  <v-icon style="transform: rotateY(180deg)" :style="{ 'margin-left': (ind - 1) * 32 + 'px' }" v-if="ind > 0">mdi-keyboard-return</v-icon>
                  <v-list-tile-title :class="{ 'ml-2': ind }">{{ option.text }}</v-list-tile-title>
                </v-list-tile>
              </v-list>
            </template>

            <template v-if="(menuOptions.options || []).length">
              <v-divider></v-divider>
              <v-list class="pb-0">
                <v-list-tile>
                  <v-list-tile-sub-title>Select an option</v-list-tile-sub-title>
                </v-list-tile>
              </v-list>

              <v-list class="py-0">
                <v-list-tile v-for="(option, ind) in menuOptions.options" @[clickOption(option)]="selectOption('options', option)" :key="ind + option.text">
                  <v-flex style="min-width: 40px">
                    <v-icon v-if="option.selected">mdi-check</v-icon>
                  </v-flex>
                  <v-list-tile-title>{{ option.text }}</v-list-tile-title>
                </v-list-tile>
              </v-list>
            </template>
          </v-card>
        </v-menu>
      </div>
    </div>
    <top-chart-search class="px-3" :data="sortedData" :text="text + menuOptionText" :prop="visualization.name_field" @input="(search = $event), scrollToCurrentSearchItem()" @close="showSearch = false" v-else />
    <v-icon>mdi-keyboard_arrow_down</v-icon>
    <div class="hvh-relative">
      <HvhChart :visualization="visualization" :options="chartOptions" :isEditMode="true" @item-clicked="$emit('checked-changed', $event, sortedData)" @wheel="scroll" ref="chart" />
      <HvhVerticalScrollBar
        :total="data.length"
        :displayed="displayedBarChartData"
        v-model="scrollBarValue"
        :markers="searchMarkers"
        v-if="sortedData.length > displayedBarChartData"
        :style="{ position: 'absolute', margin: '0', top: 0, right: 0, bottom: 0 }"
      />
      <h4 class="text-xs-center hvh-h7">{{ menuOptions.usePatientCount ? 'Patients' : 'HCPs' }}</h4>
      <loader v-if="isFiltering" />
    </div>
  </div>
</template>
  
<script>
import { customNumberFormat, Debouncer, findSearchWordSplitted, hashCode } from '../../../libraries/common';
import HvhChart from '../../charts/HvhChart.vue';
import HvhVerticalScrollBar from '../../common/scrollBar/HvhVerticalScrollBar.vue';

import { merge } from 'lodash';
import { isTextWithinLimit, splitSentanceToWords, splitWordsToLines, trimDotEnd } from '../../../libraries/charts/chartCommon';

export default {
  noExport: true,
  components: { HvhChart, HvhVerticalScrollBar },
  props: ['data', 'filterObject', 'text', 'isChecked', 'nameField', 'valueField', 'options', 'isFiltering', 'menuOptions', 'allChartTypes'],
  data() {
    return {
      displayedBarChartData: 10,
      scrollBarValue: 0,
      chartOptions: merge({
        grid: {
          bottom: '0%',
          containLabel: true,
          left: '20px',
          right: '30px',
          top: '0%'
        },
        yAxis: {
          axisLabel: {
            formatter: (value, ind) => {
              var isTextWithinLimitValue = isTextWithinLimit(this.$el.offsetWidth - 20);
              var words = splitSentanceToWords(value);

              if (!this.search.regex || !value.match(this.search.regex)) {
                const splitted = splitWordsToLines(words.map(word => ({ word })), isTextWithinLimitValue, 2);
                if (splitted[1]) {
                  splitted[1] = trimDotEnd(splitted[1], isTextWithinLimitValue);
                }
                return splitted.map(f => f.sentance).join('\n');
              } else {
                var isCurrent = this.scrollBarValue + 9 - ind === this.search.index;

                var highlightedPositions = [];
                findSearchWordSplitted(words.join(' '), this.search.regex)
                  .reduce((acc, f) => {
                    if (f.isMatch)
                      highlightedPositions.push({ from: acc, length: f.word.length });
                    return acc + f.word.length;
                  }, 0);

                var wordsWithPositions = [];
                words.reduce((from, word) => {
                  var to = from + word.length;
                  var positions = highlightedPositions.filter(f => f.from < to && f.from + f.length > from);
                  wordsWithPositions.push({
                    word,
                    from,
                    highlightedPositions: positions.map(f => ({ from: Math.max(f.from, from) - from, length: Math.min(to, f.from + f.length) - Math.max(f.from, from) }))
                  });
                  return to + 1;
                }, 0);
                var splitted = splitWordsToLines(wordsWithPositions, isTextWithinLimitValue, 2);
                if (splitted[1]) {
                  splitted[1] = trimDotEnd(splitted[1], isTextWithinLimitValue);
                }
                return splitted
                  .map((sentance) => sentance.usedWords
                    .map(f => {
                      if (f.highlightedPositions.length) {
                        var positions = [...f.highlightedPositions.flatMap(f => [f.from, f.from + f.length]), f.word.length]
                          .reduce((acc, pos) => pos > acc.last() ? [...acc, pos] : acc, [0]);

                        return positions.head()
                          .map((pos, ind) => f.highlightedPositions.find(g => g.from === pos) || { from: pos, length: positions[ind + 1] - pos, noHighlight: true })
                          .map(g => {
                            var wordPart = f.word.substring(g.from, g.from + g.length);
                            return !g.noHighlight ? `{${isCurrent ? 'highlightCurrent' : 'highlight'}|${wordPart}}` : wordPart;
                          }).join('');
                      } else return f.word
                    }).join(' ')).join('\n');
              }
            },
            rich: {
              highlight: {
                backgroundColor: 'yellow',
                height: 16
              },
              highlightCurrent: {
                backgroundColor: '#ff9632',
                height: 16
              }
            },
            inside: true,
          },
          axisTick: {
            show: false
          },
        },
        tooltip: {
          formatter: (params) => {
            var marker = '<dt style="width:10px;height:10px;display:inline-block;margin-right:5px;">' +
              '<svg viewBox="0 0 12 12"><path fill="' + params.color + '" d="M0 0h12v12H0z"></path></svg>' +
              '</dt>';

            return '<div style="max-width:400px;white-space:pre-wrap;">' +
              params.name + '<br>' +
              marker + ' HCPs: ' + customNumberFormat(params.data.data.hcpCount) + '<br>' +
              marker + ' Patients: ' + customNumberFormat(params.data.data.patientCount) + '<br>' +
              '<div>';
          }
        },
      }, (this.options || {})),
      search: {},
      showSearch: false,
      menu: false,
      skipNextFilter: false,
      debouncer: new Debouncer(2000),
    }
  },
  methods: {
    scroll(event) {
      var oldValue = this.scrollBarValue;
      this.scrollBarValue += event.wheelDeltaY < 0 ? 1 : -1;
      this.scrollBarValue = Math.max(0, Math.min(this.scrollBarValue, this.sortedData.length - this.displayedBarChartData));
      if (Math.abs(oldValue - this.scrollBarValue) > 0 || event.ctrlKey) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    scrollToCurrentSearchItem() {
      if (this.search.index > -1)
        this.scrollBarValue = this.search.index;
      this.rerender();
    },
    rerender() {
      this.$nextTick(() => this.$refs.chart.renderChart());
    },
    clickOption(option) {
      return !option.selected ? 'click' : null;
    },
    selectOption(prop, option) {
      this.$emit('update:menu-options', {
        ...this.menuOptions,
        [prop]: this.menuOptions[prop].map(f => ({ ...f, selected: f === option }))
      });
      this.skipNextFilter = true;
      this.$nextTick(() => this.$emit('load-data', this.filterObject));
    },
    setUsePatientCount(usePatientCount) {
      this.$emit('update:menu-options', { ...this.menuOptions, usePatientCount });
    },
  },
  computed: {
    sortBy() {
      return this.menuOptions.usePatientCount ? (f => f.patientCount) : (f => f.hcpCount);
    },
    sortedData() {
      return this.data.sortBy(this.sortBy).reverse();
    },
    visualization() {
      var dataLength = Math.min(this.sortedData.length, this.displayedBarChartData);
      return {
        visualization_type: 'horizontalbarchart',
        name_field: this.nameField || 'diagnosis',
        value_field: this.menuOptions.usePatientCount ? 'patientCount' : 'hcpCount',
        data: this.sortedData
          .filter((_, ind) => ind >= this.scrollBarValue && ind < this.scrollBarValue + dataLength)
          .map(f => ({
            ...f,
            ...(this.isChecked && this.isChecked(f) ? { color: '#37ae8c' } : {}),
            unchecked: this.isChecked && this.sortedData.find(f => this.isChecked(f)) && !this.isChecked(f)
          })),
      };
    },
    filterHash() {
      return hashCode(this.filterObject || {});
    },
    searchMarkers() {
      return (this.search.foundItems || []).map(f => ({ value: this.sortedData.indexOf(f) }));
    },
    menuOptionText() {
      if (!this.menuOptions.levels) return '';
      var menuOptionText = (this.menuOptions.levels.find(f => f.selected) || {}).text;
      if (!menuOptionText) return '';
      return `: ${menuOptionText.split('/')[0]}`;
    },
  },
  watch: {
    filterHash() {
      if (!this.skipNextFilter) {
        this.debouncer.clear().push(() => this.$emit('refresh-data', this.filterObject));
      } else {
        this.skipNextFilter = false;
      }
    },
  },
  mounted() {
    this.$emit('chart-created', this.filterObject);
  }
}
</script>
